import React, { useState, useEffect, useCallback } from "react";
import {
  useAccount,
  usePublicClient,
  useReadContracts,
  useWriteContract,
  useWaitForTransactionReceipt,
  useReadContract,
  useEnsName,
} from "wagmi";
import { useQueryClient } from "@tanstack/react-query";

const ERC721_ABI = [
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "tokenOfOwnerByIndex",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
];

const LYRE_ABI = [
  {
    name: "setText",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "key", type: "string" },
      { name: "value", type: "string" },
    ],
    outputs: [],
  },
  {
    name: "textRecords",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "key", type: "string" },
    ],
    outputs: [{ name: "", type: "string" }],
  },
];

const REVERSE_REGISTRAR = "0xa58E81fe9b61B5c3fE2AFD33CF304c454AbFc7Cb";
const REVERSE_REGISTRAR_ABI = [
  {
    name: "setName",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "name", type: "string" }],
    outputs: [{ name: "", type: "bytes32" }],
  },
];

const TEXT_RECORD_KEYS = [
  "url",
  "avatar",
  "description",
  "com.twitter",
  "com.github",
  "email",
];

function RecordRow({ tokenId, recordKey, lyreAddress, chainId, nftContractAddress }) {
  const [value, setValue] = useState("");

  const { data: currentValue, refetch } = useReadContract({
    address: lyreAddress,
    abi: LYRE_ABI,
    functionName: "textRecords",
    args: [tokenId, recordKey],
    chainId,
  });

  const { writeContract, data: txHash, isPending, error: writeError } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } =
    useWaitForTransactionReceipt({ hash: txHash });

  useEffect(() => {
    if (isSuccess) refetch();
  }, [isSuccess, refetch]);

  const handleSave = () => {
    writeContract({
      address: lyreAddress,
      abi: LYRE_ABI,
      functionName: "setText",
      args: [tokenId, recordKey, value],
    });
  };

  return (
    <div className="flex items-center gap-2">
      <span className="w-28 text-sm text-gray-500 dark:text-gray-400 font-mono shrink-0">
        {recordKey}:
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={currentValue || "not set"}
        className="flex-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono placeholder:text-gray-400 dark:placeholder:text-gray-600"
      />
      {recordKey === "avatar" && nftContractAddress && (
        <button
          onClick={() => setValue(`eip155:${chainId}/erc721:${nftContractAddress}/${tokenId.toString()}`)}
          className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors shrink-0"
          title="Use the NFT's own image as avatar"
        >
          Use NFT image
        </button>
      )}
      <button
        onClick={handleSave}
        disabled={isPending || isConfirming || !value}
        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
      >
        {isPending ? "Confirm..." : isConfirming ? "Saving..." : "Save"}
      </button>
      {isSuccess && (
        <span className="text-green-600 dark:text-green-400 text-xs shrink-0">Saved</span>
      )}
      {writeError && (
        <span className="text-red-600 dark:text-red-400 text-xs shrink-0" title={writeError.shortMessage || writeError.message}>
          Error
        </span>
      )}
    </div>
  );
}

function TokenPanel({ tokenId, ensName, lyreAddress, chainId, nftContractAddress, primaryName, onPrimarySet }) {
  const fullName = `${tokenId.toString()}.${ensName}`;
  const isPrimary = primaryName === fullName;

  const {
    writeContract,
    data: txHash,
    isPending,
    error: writeError,
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } =
    useWaitForTransactionReceipt({ hash: txHash });

  useEffect(() => {
    if (isSuccess && onPrimarySet) onPrimarySet();
  }, [isSuccess, onPrimarySet]);

  const handleSetPrimary = () => {
    writeContract({
      address: REVERSE_REGISTRAR,
      abi: REVERSE_REGISTRAR_ABI,
      functionName: "setName",
      args: [fullName],
    });
  };

  return (
    <div className="ml-6 border-l-2 border-gray-200 dark:border-gray-700 pl-4 py-3 space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">{fullName}</h4>
        {isPrimary ? (
          <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 rounded text-xs font-medium">
            Primary
          </span>
        ) : (
          <button
            onClick={handleSetPrimary}
            disabled={isPending || isConfirming}
            className="px-3 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPending
              ? "Confirm in wallet..."
              : isConfirming
              ? "Setting..."
              : "Set as primary ENS name"}
          </button>
        )}
        {isSuccess && !isPrimary && (
          <span className="text-green-600 dark:text-green-400 text-xs">Primary name set!</span>
        )}
        {writeError && (
          <span className="text-red-600 dark:text-red-400 text-xs">
            {writeError.shortMessage || writeError.message}
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {TEXT_RECORD_KEYS.map((key) => (
          <RecordRow
            key={key}
            tokenId={tokenId}
            recordKey={key}
            lyreAddress={lyreAddress}
            chainId={chainId}
            nftContractAddress={nftContractAddress}
          />
        ))}
      </div>
    </div>
  );
}

function CollectionCard({ collection, address, primaryName, onPrimarySet }) {
  const [expanded, setExpanded] = useState(false);
  const [tokenIds, setTokenIds] = useState([]);
  const [enumFailed, setEnumFailed] = useState(false);
  const [manualInput, setManualInput] = useState("");

  const chainId = collection.chain_id;
  const publicClient = usePublicClient({ chainId });

  const { data: balance, isLoading: balanceLoading, error: balanceError } = useReadContract({
    address: collection.nft_contract_address,
    abi: ERC721_ABI,
    functionName: "balanceOf",
    args: [address],
    chainId,
  });

  const count = balance ? Number(balance) : 0;

  useEffect(() => {
    if (!expanded || count === 0 || !publicClient || !address) return;
    if (tokenIds.length > 0 || enumFailed) return;

    const fetchTokens = async () => {
      const ids = [];
      for (let i = 0; i < count && i < 20; i++) {
        try {
          const tokenId = await publicClient.readContract({
            address: collection.nft_contract_address,
            abi: ERC721_ABI,
            functionName: "tokenOfOwnerByIndex",
            args: [address, BigInt(i)],
          });
          ids.push(tokenId);
        } catch {
          setEnumFailed(true);
          return;
        }
      }
      setTokenIds(ids);
    };
    fetchTokens();
  }, [expanded, count, publicClient, address, collection.nft_contract_address, tokenIds.length, enumFailed]);

  if (!balanceLoading && !balanceError && count === 0) return null;

  const handleAddToken = () => {
    const id = manualInput.trim();
    if (!id || isNaN(id)) return;
    const bigId = BigInt(id);
    if (!tokenIds.some((t) => t === bigId)) {
      setTokenIds((prev) => [...prev, bigId]);
    }
    setManualInput("");
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-left"
      >
        <span className="text-lg font-semibold text-gray-900 dark:text-white">
          {collection.ens_name}
        </span>
        <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          {balanceLoading ? "Loading..." : `${count} token${count !== 1 ? "s" : ""}`}
          <span className="text-lg">{expanded ? "\u25BC" : "\u25B6"}</span>
        </span>
      </button>
      {expanded && (
        <div className="px-5 py-4 space-y-4 bg-white dark:bg-gray-900">
          {balanceError && (
            <p className="text-red-600 dark:text-red-400 text-sm">
              Failed to check token balance. Make sure your wallet is connected to Ethereum mainnet.
            </p>
          )}
          {enumFailed && (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddToken()}
                placeholder="Enter token ID..."
                className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded px-3 py-2 text-sm font-mono placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
              <button
                onClick={handleAddToken}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Add Token
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                You own {count} token{count !== 1 ? "s" : ""} (enumeration not supported — enter IDs manually)
              </span>
            </div>
          )}
          {!enumFailed && tokenIds.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading tokens...</p>
          )}
          {tokenIds.map((tokenId) => (
            <TokenPanel
              key={tokenId.toString()}
              tokenId={tokenId}
              ensName={collection.ens_name}
              lyreAddress={collection.lyre_contract_address}
              chainId={chainId}
              nftContractAddress={collection.nft_contract_address}
              primaryName={primaryName}
              onPrimarySet={onPrimarySet}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ManageSubnames() {
  const { address, isConnected } = useAccount();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const { data: primaryName } = useEnsName({ address });

  const handlePrimarySet = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["ensName"] });
    queryClient.invalidateQueries({ queryKey: ["ensAvatar"] });
  }, [queryClient]);

  useEffect(() => {
    fetch("/api/collections")
      .then((res) => res.json())
      .then((data) => {
        setCollections(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (!isConnected) {
    return (
      <div className="text-center py-16">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Your Subnames</h1>
        <p className="text-gray-500 dark:text-gray-400 text-lg">
          Connect your wallet to manage your subname records.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Your Subnames</h1>
        <p className="text-gray-500 dark:text-gray-400">Loading collections...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Your Subnames</h1>
      {collections.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No collections with deployed Lyre contracts found.</p>
      ) : (
        <div className="space-y-4">
          {collections.map((c) => (
            <CollectionCard key={c.id} collection={c} address={address} primaryName={primaryName} onPrimarySet={handlePrimarySet} />
          ))}
        </div>
      )}
    </div>
  );
}
